import type { ReactNode } from "react";
import { IconCalendarDollar, IconFileDollar } from "@xefi/x-react/icons";
import { IconChevronDown, IconUsersGroup } from "@tabler/icons-react";
import type { Item } from "@xefi/x-react/navbar";

import { ModuleTab } from "../../domain/models/User";
import { NavigationSubItemIcon } from "./NavigationSubItemIcon";

type TFunction = (key: string) => string;

interface CreateNavigationItemsProps {
  pathname?: string | null;
  t: TFunction;
  tabs?: ModuleTab[];
  canManageUserAccess?: boolean;
  canAccessPayments?: boolean;
}

type NavigationSection = {
  key: string;
  label: string;
  href: string;
  icon: ReactNode;
  archivesHref?: string;
  isOpen: boolean;
};

/**
 * Une rubrique et son éventuel sous-menu Archives, déplié tant que l'utilisateur
 * se trouve dans la rubrique. Un seul élément est actif à la fois : sur la page
 * Archives, c'est le sous-élément et non la rubrique qui est mis en avant.
 */
const createSectionItems = (
  section: NavigationSection,
  pathname: string | null | undefined,
  t: TFunction,
): Item[] => {
  if (!section.archivesHref) {
    return [
      {
        key: section.key,
        label: section.label,
        href: section.href,
        startContent: section.icon,
        isActive: section.isOpen,
      },
    ];
  }

  const isOnArchives = pathname === section.archivesHref;

  const sectionItem: Item = {
    key: section.key,
    label: section.label,
    href: section.href,
    startContent: section.icon,
    endContent: (
      <IconChevronDown
        size={16}
        aria-hidden
        className={`transition-transform duration-200 ${section.isOpen ? "" : "-rotate-90"}`}
      />
    ),
    isActive: section.isOpen && !isOnArchives,
  };

  if (!section.isOpen) {
    return [sectionItem];
  }

  return [
    sectionItem,
    {
      key: `${section.key}-archives`,
      label: t("archive.navLabel"),
      href: section.archivesHref,
      startContent: <NavigationSubItemIcon />,
      isActive: isOnArchives,
    },
  ];
};

export const createNavigationItems = ({
  t,
  pathname,
  tabs,
  canManageUserAccess = false,
  canAccessPayments = false,
}: CreateNavigationItemsProps): Item[] => {
  const canSeeTransfers = !tabs || tabs.includes("requests");

  const isPaymentsSection =
    pathname === "/" || (pathname?.startsWith("/payments-received") ?? false);
  const isTransfersSection =
    pathname === "/transfers" ||
    (pathname?.startsWith("/transfers/") ?? false) ||
    (pathname?.startsWith("/demands") ?? false);
  const isUserAccessSection =
    pathname?.startsWith("/settings/user-access") ?? false;

  const items: Item[] = [];

  if (canAccessPayments) {
    items.push(
      ...createSectionItems(
        {
          key: "payments",
          label: t("payments.received"),
          href: "/",
          icon: <IconFileDollar />,
          isOpen: isPaymentsSection,
        },
        pathname,
        t,
      ),
    );
  }

  if (canSeeTransfers) {
    items.push(
      ...createSectionItems(
        {
          key: "transfers",
          label: t("payments.toEmit"),
          href: "/transfers",
          icon: <IconCalendarDollar />,
          archivesHref: "/transfers/archives",
          isOpen: isTransfersSection,
        },
        pathname,
        t,
      ),
    );
  }

  if (canManageUserAccess) {
    items.push({
      key: "user-access",
      label: t("userAccess.navLabel"),
      href: "/settings/user-access",
      startContent: <IconUsersGroup />,
      isActive: isUserAccessSection,
    });
  }

  return items;
};
